import type { InferenceModelMetadata, InferenceModelType } from "@kuzco/models";
import { INFERENCE_MODEL_METADATA } from "@kuzco/models";

import { ModelUtils } from "../models/ModelUtils";
import { ModelPricingUtils } from "../pricing/ModelPricingUtils";

export type ModelWithPricing = {
  model: InferenceModelType;
  promptPriceInHogansPerToken: bigint;
  completionPriceInHogansPerToken: bigint;
  metadata: InferenceModelMetadata;
};

export type ModelByFamilyWithPricing = InferenceModelMetadata & {
  model: InferenceModelType;
  promptPriceInHogansPerToken: bigint;
  completionPriceInHogansPerToken: bigint;
};

function getMetadata(model: InferenceModelType): InferenceModelMetadata {
  const metadata = INFERENCE_MODEL_METADATA[model];
  if (!metadata) {
    throw new Error(`No metadata found for model: ${model}`);
  }
  return metadata;
}

export const MODELS_WITH_PRICING: ModelWithPricing[] =
  ModelUtils.PUBLIC_MODELS.map((model) => {
    const promptPriceInHogansPerToken =
      ModelPricingUtils.getPromptPricePerTokenInHogansForModel(model);
    const completionPriceInHogansPerToken =
      ModelPricingUtils.getCompletionPricePerTokenInHogansForModel(model);
    const metadata = getMetadata(model);
    const result: ModelWithPricing = {
      model,
      promptPriceInHogansPerToken,
      completionPriceInHogansPerToken,
      metadata,
    };
    return result;
  });

export const PLAYGROUND_MODELS_WITH_PRICING: ModelWithPricing[] =
  ModelUtils.PLAYGROUND_MODELS.map((model) => {
    const promptPriceInHogansPerToken =
      ModelPricingUtils.getPromptPricePerTokenInHogansForModel(model);
    const completionPriceInHogansPerToken =
      ModelPricingUtils.getCompletionPricePerTokenInHogansForModel(model);
    const metadata = getMetadata(model);
    const result: ModelWithPricing = {
      model,
      promptPriceInHogansPerToken,
      completionPriceInHogansPerToken,
      metadata,
    };
    return result;
  });

export const MODELS_BY_FAMILY_WITH_PRICING: ModelByFamilyWithPricing[] =
  ModelUtils.PUBLIC_MODELS.map((model) => {
    const promptPriceInHogansPerToken =
      ModelPricingUtils.getPromptPricePerTokenInHogansForModel(model);
    const completionPriceInHogansPerToken =
      ModelPricingUtils.getCompletionPricePerTokenInHogansForModel(model);
    const metadata = getMetadata(model);
    const result: ModelByFamilyWithPricing = {
      model,
      promptPriceInHogansPerToken,
      completionPriceInHogansPerToken,
      ...metadata,
    };
    return result;
  });

// Includes models with `is_model_supported: true` regardless of
// `is_model_public`. Use this on routes that intentionally render
// non-public-but-supported models (e.g. the retired schematron v1
// marketing pages, which are kept reachable so visitors land on the
// retirement banner instead of a 404). For anything driven by the
// public catalog, prefer `MODELS_WITH_PRICING` /
// `MODELS_BY_FAMILY_WITH_PRICING`.
export const SUPPORTED_MODELS_WITH_PRICING: ModelWithPricing[] =
  ModelUtils.SUPPORTED_MODELS.map((model) => {
    const promptPriceInHogansPerToken =
      ModelPricingUtils.getPromptPricePerTokenInHogansForModel(model);
    const completionPriceInHogansPerToken =
      ModelPricingUtils.getCompletionPricePerTokenInHogansForModel(model);
    const metadata = getMetadata(model);
    const result: ModelWithPricing = {
      model,
      promptPriceInHogansPerToken,
      completionPriceInHogansPerToken,
      metadata,
    };
    return result;
  });

export const SUPPORTED_MODELS_BY_FAMILY_WITH_PRICING: ModelByFamilyWithPricing[] =
  ModelUtils.SUPPORTED_MODELS.map((model) => {
    const promptPriceInHogansPerToken =
      ModelPricingUtils.getPromptPricePerTokenInHogansForModel(model);
    const completionPriceInHogansPerToken =
      ModelPricingUtils.getCompletionPricePerTokenInHogansForModel(model);
    const metadata = getMetadata(model);
    const result: ModelByFamilyWithPricing = {
      model,
      promptPriceInHogansPerToken,
      completionPriceInHogansPerToken,
      ...metadata,
    };
    return result;
  });
